import { userApi } from '@/lib/api/user';
import { workspaceApi } from '@/lib/api/workspace';
import type { WorkspaceWithRole } from '@/lib/api/workspace';
import type { Status, Priority, Tag, WorkspaceCreate } from "dico-types";
import { create } from 'zustand';

export type WorkspaceInitState = 'idle' | 'loading' | 'loaded' | 'user-not-found' | 'error';

/** Statuses per workspace id; populated during init and when creating a workspace. */
export type StatusesByWorkspaceId = Record<string, Status[]>;
/** Priorities per workspace id; populated during init and when creating a workspace. */
export type PrioritiesByWorkspaceId = Record<string, Priority[]>;
/** Tags per workspace id; populated during init and when creating a workspace. */
export type TagsByWorkspaceId = Record<string, Tag[]>;

interface WorkspaceStoreState {
  workspaces: WorkspaceWithRole[];
  /** Statuses keyed by workspace id. Loaded with workspaces on sign-in. */
  statusesByWorkspaceId: StatusesByWorkspaceId;
  /** Priorities keyed by workspace id. Loaded with workspaces on sign-in. */
  prioritiesByWorkspaceId: PrioritiesByWorkspaceId;
  /** Tags keyed by workspace id. Loaded with workspaces on sign-in. */
  tagsByWorkspaceId: TagsByWorkspaceId;
  initState: WorkspaceInitState;
  init: () => Promise<void>;
  create: (data: WorkspaceCreate) => Promise<WorkspaceWithRole>;
  /** Get statuses for a workspace (from store; empty until init has run). */
  getStatuses: (workspaceId: string) => Status[];
  /** Get priorities for a workspace (from store; empty until init has run). */
  getPriorities: (workspaceId: string) => Priority[];
  /** Get tags for a workspace (from store; empty until init has run). */
  getTags: (workspaceId: string) => Tag[];
  /** Refetch statuses for a workspace (e.g. after editing in settings). */
  refreshStatuses: (workspaceId: string) => Promise<void>;
  /** Refetch priorities for a workspace (e.g. after editing in settings). */
  refreshPriorities: (workspaceId: string) => Promise<void>;
  /** Refetch tags for a workspace (e.g. after editing in settings). */
  refreshTags: (workspaceId: string) => Promise<void>;
  reset: () => void;
}

export const useWorkspaceStore = create<WorkspaceStoreState>((set, get) => ({
  workspaces: [],
  statusesByWorkspaceId: {},
  prioritiesByWorkspaceId: {},
  tagsByWorkspaceId: {},
  initState: 'idle',

  init: async () => {
    if (get().initState !== 'idle') return;
    set({ initState: 'loading' });

    try {
      const userRes = await userApi.getMe();
      if (!userRes.success || !userRes.data) {
        set({ initState: 'user-not-found' });
        return;
      }

      const workspaceRes = await workspaceApi.getWorkspaces();
      const workspaces = workspaceRes.data ?? [];

      const statusesByWorkspaceId: StatusesByWorkspaceId = {};
      const prioritiesByWorkspaceId: PrioritiesByWorkspaceId = {};
      const tagsByWorkspaceId: TagsByWorkspaceId = {};
      await Promise.all(
        workspaces.map(async (w) => {
          const [statusRes, priorityRes, tagRes] = await Promise.all([
            workspaceApi.getWorkspaceStatuses(w.id),
            workspaceApi.getWorkspacePriorities(w.id),
            workspaceApi.getWorkspaceTags(w.id)
          ]);
          statusesByWorkspaceId[w.id] = statusRes.data ?? [];
          prioritiesByWorkspaceId[w.id] = priorityRes.data ?? [];
          tagsByWorkspaceId[w.id] = tagRes.data ?? [];
        })
      );

      set({ workspaces, statusesByWorkspaceId, prioritiesByWorkspaceId, tagsByWorkspaceId, initState: 'loaded' });
    } catch {
      set({ initState: 'error' });
    }
  },

  create: async (data: WorkspaceCreate) => {
    const res = await workspaceApi.createWorkspace(data);
    if (!res.success || !res.data) {
      throw new Error(res.error ?? 'Failed to create workspace');
    }
    const workspace = res.data;
    const [statusRes, priorityRes, tagRes] = await Promise.all([
      workspaceApi.getWorkspaceStatuses(workspace.id),
      workspaceApi.getWorkspacePriorities(workspace.id),
      workspaceApi.getWorkspaceTags(workspace.id)
    ]);
    const statuses = statusRes.data ?? [];
    const priorities = priorityRes.data ?? [];
    const tags = tagRes.data ?? [];
    set((s) => ({
      workspaces: [...s.workspaces, workspace],
      statusesByWorkspaceId: { ...s.statusesByWorkspaceId, [workspace.id]: statuses },
      prioritiesByWorkspaceId: { ...s.prioritiesByWorkspaceId, [workspace.id]: priorities },
      tagsByWorkspaceId: { ...s.tagsByWorkspaceId, [workspace.id]: tags }
    }));
    return workspace;
  },

  getStatuses: (workspaceId: string) => {
    return get().statusesByWorkspaceId[workspaceId] ?? [];
  },

  getPriorities: (workspaceId: string) => {
    return get().prioritiesByWorkspaceId[workspaceId] ?? [];
  },

  getTags: (workspaceId: string) => {
    return get().tagsByWorkspaceId[workspaceId] ?? [];
  },

  refreshStatuses: async (workspaceId: string) => {
    try {
      const res = await workspaceApi.getWorkspaceStatuses(workspaceId);
      const list = res.data ?? [];
      set((s) => ({
        statusesByWorkspaceId: { ...s.statusesByWorkspaceId, [workspaceId]: list }
      }));
    } catch {
      // Keep existing data on error
    }
  },

  refreshPriorities: async (workspaceId: string) => {
    try {
      const res = await workspaceApi.getWorkspacePriorities(workspaceId);
      const list = res.data ?? [];
      set((s) => ({
        prioritiesByWorkspaceId: { ...s.prioritiesByWorkspaceId, [workspaceId]: list }
      }));
    } catch {
      // Keep existing data on error
    }
  },

  refreshTags: async (workspaceId: string) => {
    try {
      const res = await workspaceApi.getWorkspaceTags(workspaceId);
      const list = res.data ?? [];
      set((s) => ({
        tagsByWorkspaceId: { ...s.tagsByWorkspaceId, [workspaceId]: list }
      }));
    } catch {
      // Keep existing data on error
    }
  },

  reset: () => set({ workspaces: [], statusesByWorkspaceId: {}, prioritiesByWorkspaceId: {}, tagsByWorkspaceId: {}, initState: 'idle' })
}));
